/**
 * Local-tick countdown anchored to a server-supplied `msRemaining`.
 *
 * The server sends `msRemaining` only on ROOM_STATE_SYNC (snapshot push
 * at phase boundaries + targeted snapshots). Between snapshots the
 * client must derive the remaining time itself, otherwise the on-screen
 * timer freezes at whatever value the last snapshot carried.
 *
 * Pattern: snapshot anchors `{at: now, msRemaining}`; a 250ms interval
 * recomputes `remaining = msRemaining - (now - at)`. Each new snapshot
 * value re-anchors. Cosmetic only, the server's PHASE_END is the
 * authoritative phase boundary.
 *
 * Returns whole seconds remaining (ceil), bounded at 0.
 *
 * Codex M4 review P2-11: WordPickScreen used the raw `msRemaining`
 * value, so its countdown was frozen for the entire wordPick phase.
 * DrawingPhaseShell and RevealPhaseShell already had inline copies of
 * this pattern; this hook unifies all three call sites (Boy Scout +
 * 3-strike extraction).
 */

import { useEffect, useRef, useState } from "preact/hooks";

export function useDeadlineCountdown(msRemaining: number): number {
  const [secondsLeft, setSecondsLeft] = useState(() => Math.max(0, Math.ceil(msRemaining / 1000)));
  const anchorRef = useRef({ at: Date.now(), msRemaining });

  useEffect(() => {
    anchorRef.current = { at: Date.now(), msRemaining };
    setSecondsLeft(Math.max(0, Math.ceil(msRemaining / 1000)));
    const id = setInterval(() => {
      const elapsed = Date.now() - anchorRef.current.at;
      const remaining = anchorRef.current.msRemaining - elapsed;
      setSecondsLeft(Math.max(0, Math.ceil(remaining / 1000)));
    }, 250);
    return () => clearInterval(id);
  }, [msRemaining]);

  return secondsLeft;
}
